import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Displays the total price, deposit, and balance for a Brillio "formula" product,
 * keeping them in sync with the selected variant.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} total
 * @property {HTMLElement} deposit
 * @property {HTMLElement} balance
 * @property {HTMLElement} [depositNote]
 *
 * @extends {Component<Refs>}
 */
class FormulaTotalsComponent extends Component {
  requiredRefs = ['total', 'deposit', 'balance'];

  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * @param {number} cents
   */
  #formatMoney(cents) {
    return (cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '\u00A0\u20AC';
  }

  /**
   * @param {CustomEvent} event
   */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const { newProduct, resource } = detail;
        if (newProduct) {
          this.dataset.productId = newProduct.id;
        }
        if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }
        if (!resource || typeof resource.price === 'undefined') return;

        const depositCents = parseInt(this.dataset.depositCents ?? '2000', 10);
        const balanceCents = resource.price - depositCents;

        this.refs.total.textContent = this.#formatMoney(resource.price);
        this.refs.balance.textContent = this.#formatMoney(balanceCents);
        // Deposit is fixed regardless of variant, so refs.deposit / depositNote don't need updating.
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[formula-totals] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('formula-totals-component')) {
  customElements.define('formula-totals-component', FormulaTotalsComponent);
}
